import {usePortalTooltip} from '@github-ui/portal-tooltip/use-portal-tooltip'
import {testIdProps} from '@github-ui/test-id-props'
import {clsx} from 'clsx'
import {addDays, addHours} from 'date-fns'
import {useMemo, useRef} from 'react'

import useIsVisible from '../../../components/board/hooks/use-is-visible'
import {dateStringFromISODate} from '../../../helpers/date-string-from-iso-string'
import {intervalDateRangeFromUTC} from '../../../helpers/iterations'
import {getIterationOptionsTooltipText} from '../../../helpers/roadmap-helpers'
import {
  isCurrentIterationByMarker,
  type RoadmapMarker,
  RoadmapMarkerType,
  useRoadmapMarkersInRange,
} from '../components/roadmap-markers-in-range'
import {useRoadmapView} from '../roadmap-view-provider'
import {getTooltipText} from '../tooltip-helper'
import styles from './roadmap-marker.module.css'

/**
 * Renders the small circles (or other shapes) at the top of a marker, at the bottom edge of the header
 * Currently includes the "Today" marker, but could be expanded to include other markers like "Iteration X".
 * */
export function RoadmapMarkerHeaderNubs() {
  return (
    <div role="row" className={styles.Box}>
      <TodayMarkerNub />
      <MarkerNubs />
    </div>
  )
}

function TodayMarkerNub() {
  const {getX, today} = useRoadmapView()
  const {singleDayMarkerMap} = useRoadmapMarkersInRange()

  const midday = useMemo(() => addHours(today, 12), [today])
  const leftOffset = getX(midday) - 3.5

  const ref = useRef<HTMLTimeElement | null>(null)
  const {isVisible} = useIsVisible({ref})
  const [contentProps, tooltip] = usePortalTooltip({
    contentRef: ref,
    'aria-label': `Today: ${getTooltipText({start: today, end: today})}`,
  })

  const todayDateString = dateStringFromISODate(today.toISOString())
  // if a single day marker is already present, don't render the today marker
  const isSingleDateMarkerPresent = singleDayMarkerMap.has(todayDateString)

  return (
    <time
      // eslint-disable-next-line jsx-a11y/no-noninteractive-element-to-interactive-role
      role="gridcell"
      dateTime={todayDateString}
      ref={ref}
      className={clsx(
        styles.markerNubBase,
        isVisible && !isSingleDateMarkerPresent ? styles.todayNub : styles.invisibleNub,
      )}
      style={{left: `${leftOffset}px`}}
      {...contentProps}
      {...testIdProps(`roadmap-today-marker-nub`)}
    >
      {tooltip}
    </time>
  )
}

function MarkerNubs() {
  const {markersInRange} = useRoadmapMarkersInRange()

  return (
    <>
      {markersInRange.map(marker => (
        <MarkerNub marker={marker} key={marker.id} isCurrent={isCurrentIterationByMarker(marker)} />
      ))}
    </>
  )
}

const markerNubStyleMap = {
  [RoadmapMarkerType.CustomDate]: clsx(styles.markerNubBase, styles.customDateNub),
  [RoadmapMarkerType.Iteration]: clsx(styles.markerNubBase, styles.iterationNub),
  [RoadmapMarkerType.IterationBreak]: clsx(styles.markerNubBase, styles.iterationNub, styles.breakIterationNub),
  [RoadmapMarkerType.Milestone]: clsx(styles.markerNubBase, styles.milestoneNub),
}

function MarkerNub({isCurrent = false, marker}: {isCurrent?: boolean; marker: RoadmapMarker}) {
  const {getX, today} = useRoadmapView()

  const {startDate, endDate} = useMemo(() => {
    const markerStartDate = 'duration' in marker ? new Date(marker.date) : addHours(new Date(marker.date), 12)
    const markerEndDate = 'duration' in marker ? addDays(markerStartDate, marker.duration) : new Date(marker.date)
    return {
      startDate: markerStartDate,
      endDate: markerEndDate,
    }
  }, [marker])

  const leftOffset = getX(new Date(startDate)) - 3
  const ref = useRef<HTMLTimeElement | null>(null)
  const {isVisible} = useIsVisible({ref})
  const todayDateString = dateStringFromISODate(today.toISOString())
  // is today and single day marker
  const isToday = marker.date === todayDateString && !('duration' in marker)

  const {startDate: tooltipStart, endDate: tooltipEnd} =
    'duration' in marker
      ? intervalDateRangeFromUTC({
          startDate: marker.date,
          duration: marker.duration,
        })
      : {startDate, endDate}

  const tooltipText =
    'duration' in marker && marker.options.length > 1
      ? getIterationOptionsTooltipText(marker.options)
      : `${marker.title}: ${getTooltipText({
          start: tooltipStart,
          end: tooltipEnd,
        })}`

  const [contentProps, tooltip] = usePortalTooltip({
    contentRef: ref,
    'aria-label': tooltipText,
  })

  const className = isCurrent
    ? clsx(styles.markerNubBase, styles.iterationNub, styles.currentIterationNub)
    : markerNubStyleMap[marker.type]

  return (
    <time
      // eslint-disable-next-line jsx-a11y/no-noninteractive-element-to-interactive-role
      role="gridcell"
      dateTime={marker.date}
      ref={ref}
      className={clsx(className, isToday && styles.markerNubToday)}
      style={{
        left: `${leftOffset}px`,
        ...(isVisible ? {} : {backgroundColor: 'transparent', borderColor: 'transparent'}),
      }}
      {...contentProps}
      {...testIdProps(`${marker.type}-marker-nub-${marker.id}`)}
    >
      {tooltip}
    </time>
  )
}
